import { initPlayer } from "../entities/player.js";
import { createLevel } from "../systems/level.js";
import { showLightingOverlay } from "../systems/lighting.js";
import { getRoomAt } from "../systems/rooms.js";
import {
    toggleMemoryUI,
    openMemoryUI,
    resetMemoryUIForScene,
    setMemoryTutorialLock,
    isMemoryTutorialLocked,
} from "../ui/memoryUI.js";
import { toggleEscapeUI, isEscapeUIOpen, resetEscapeUIForScene } from "../ui/escapeUI.js";
import { addGameHud, resetGameHud, revealGameHud } from "../ui/gameHud.js";
import { initDebugHUD } from "../ui/debugHUD.js";
import { hasActiveAlarmLockdown, updateAlarmLockdowns } from "../systems/alarmLockdown.js";
import { AUDIO_MUSIC, getStoryMusic, playMusic, resetAudioForScene } from "../systems/audio.js";
import { initTextboxUI, resetTextboxUIForScene, queueTextboxSequence, closeTextbox, isTextboxOpen } from "../ui/textboxUI.js";
import { isDebugModeVisible } from "../systems/debugState.js";
import { isLoaded } from "../systems/memory.js";

const ROOM_MUSIC_BY_ID = new Map([
    [1, AUDIO_MUSIC.hiddenCavern],
    [8, AUDIO_MUSIC.livelyCity],
]);

function getRequestedGameMusic() {
    if (hasActiveAlarmLockdown()) return AUDIO_MUSIC.battleDefault;

    const storyMusic = getStoryMusic();
    if (storyMusic) return storyMusic;

    const player = get("player")?.[0];
    if (!player?.exists?.() || player.isDead) return null;

    const room = getRoomAt(player.pos);
    return ROOM_MUSIC_BY_ID.get(room?.id) || AUDIO_MUSIC.darkFactory;
}

export function registerGameScene() {
    scene("game", async (args) => {
        showLightingOverlay();
        resetMemoryUIForScene();
        resetTextboxUIForScene();
        resetEscapeUIForScene();
        resetGameHud();
        resetAudioForScene();

        createLevel();
        const isRespawn = !!(args && args.respawnFromCheckpoint);
        const respawnNeedsMemoryRecovery = isRespawn && (!isLoaded("core_locomotion") || !isLoaded("core_vision"));
        const tutorialActive = (!!(args && args.memoryTutorialLock) && !isRespawn) || respawnNeedsMemoryRecovery;

        initPlayer({
            respawnFromCheckpoint: isRespawn,
            tutorialActive,
        });
        initDebugHUD();
        setMemoryTutorialLock(tutorialActive);
        initTextboxUI();

        if (args?.openMemoryOnInit === true || respawnNeedsMemoryRecovery) {
            wait(0.15, () => {
                openMemoryUI();
            });
        }

        onKeyPress("space", () => {
            if (isEscapeUIOpen() || isTextboxOpen()) return;
            toggleMemoryUI();
        });

        onKeyPress("escape", () => {
            if (isMemoryTutorialLocked()) return;
            if (isTextboxOpen()) {
                closeTextbox();
                return;
            }
            toggleEscapeUI();
        });

        // Debug-only mock textbox sequence (backtick toggles debug HUD)
        onKeyPress("m", () => {
            if (!isDebugModeVisible()) return;
            queueTextboxSequence([
                {
                    tag: "OBJ_SCAN // RESTRAINT_CLAMP",
                    channel: "CH.02",
                    text: "Steel, corroded at the hinge. A sustained bite could shear it.",
                    variant: "system",
                },
                {
                    tag: "MEMORY FRAGMENT // UNINDEXED",
                    channel: "03/12",
                    text: "...the leash again. His hand, always low, like he expected me to flinch. I never did.",
                    variant: "memory",
                },
                {
                    tag: "OBJ_SCAN // CAGE_LATCH",
                    channel: "DENIED",
                    text: "Requires BITE module. RAM slot empty.",
                    variant: "locked",
                },
            ]);
        });

        onUpdate(() => {
            updateAlarmLockdowns();
            playMusic(getRequestedGameMusic());
        });

        addGameHud();
        if (isRespawn) {
            revealGameHud();
        }
    });
}
